import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import LangSwitch from "@/components/LangSwitch";
import { Leaf, ArrowCircleLeft, DownloadSimple, PencilSimple, Sparkle, ChartBar, Palette, UploadSimple, Trash, Image, Translate, Rows, Lock, Lightning } from "@phosphor-icons/react";
import GenerationProgress from "@/components/GenerationProgress";
import BlurredPreview from "@/components/BlurredPreview";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReportView() {
  const { t, lang } = useLang();
  const { id } = useParams();
  const navigate = useNavigate();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingLang, setDownloadingLang] = useState(null);
  const [regenerating, setRegenerating] = useState(false);
  const [regenElapsed, setRegenElapsed] = useState(0);
  const [regenPhase, setRegenPhase] = useState(null);
  const [regenStatus, setRegenStatus] = useState("");
  const [translating, setTranslating] = useState(false);
  const [ceoBusy, setCeoBusy] = useState(false);
  const [ceoMsg, setCeoMsg] = useState("");
  const handleRegenerateCeo = async () => {
    setCeoBusy(true); setCeoMsg("");
    try {
      await api.regenerateCeoLetter(id);
      setCeoMsg(lang === "ar" ? "تمت إعادة كتابة رسالة الرئيس التنفيذي. سيكون ملف PDF جاهزاً خلال دقيقة تقريباً." : "CEO letter rewritten. Your PDFs will be ready in about a minute.");
    } catch (e) {
      setCeoMsg(e?.response?.data?.detail?.message || e?.response?.data?.detail || (lang === "ar" ? "تعذّر إعادة الكتابة، حاول مرة أخرى." : "Could not rewrite the letter, please try again."));
    } finally { setCeoBusy(false); }
  };
  const [showBranding, setShowBranding] = useState(false);
  const [brandColors, setBrandColors] = useState({ primary_color: "#01AF9B", secondary_color: "#8B5CF6", accent_color: "#B94B05" });
  const [coverColor, setCoverColor] = useState("#73324B");
  const [coverStyle, setCoverStyle] = useState("solid");
  const [coverImageFile, setCoverImageFile] = useState(null);
  const [coverImageName, setCoverImageName] = useState("");
  const [logoFile, setLogoFile] = useState(null);
  const [logoName, setLogoName] = useState("");
  const [savingBrand, setSavingBrand] = useState(false);
  // Freemium (Phase 2, Feb 2026)
  const [sub, setSub] = useState(null);
  const [showBlurredPreview, setShowBlurredPreview] = useState(false);
  // A user is in the freemium locked state when they are NOT paid,
  // NOT grandfathered, and this specific report IS their active free
  // draft (server-tracked). In that case downloads are hidden and
  // the blurred preview modal is available.
  const isFreeDraftLocked = !!(sub && !sub.active && !sub.grandfathered
    && sub.active_free_draft_id && String(sub.active_free_draft_id) === String(id));

  useEffect(() => {
    api.getReport(id).then(r => {
      setReport(r);
      if (r.branding) {
        setBrandColors({
          primary_color: r.branding.primary_color || "#01AF9B",
          secondary_color: r.branding.secondary_color || "#8B5CF6",
          accent_color: r.branding.accent_color || "#B94B05"
        });
        if (r.branding.logo_filename) setLogoName(r.branding.logo_filename);
      }
      if (r.company_data?.cover_color) setCoverColor(r.company_data.cover_color);
      if (r.company_data?.cover_style) setCoverStyle(r.company_data.cover_style);
    }).catch(() => navigate("/dashboard")).finally(() => setLoading(false));
    // Fetch subscription/freemium status so we can lock downloads and
    // pop the blurred preview modal on first view for free drafts.
    const token = localStorage.getItem("esg_token");
    axios.get(`${API}/subscription/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setSub(r.data))
      .catch(() => setSub({ active: false }));
  }, [id, navigate]);

  // The BlurredPreview modal is deprecated in favour of the Light Preview
  // Edition PDF (Session 24, Sep 2026). We intentionally do NOT auto-open
  // it any more — free users are now served the downloadable Preview
  // Edition PDF via the top button and the bottom CTA card. The
  // BlurredPreview component is kept as dormant fallback code (deleteable
  // once the light-PDF flow is battle-tested in production).

  const handleDownloadLightPdf = async () => {
    const token = localStorage.getItem("esg_token");
    setDownloading(true);
    try {
      const resp = await fetch(`${API}/reports/${id}/light-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        let msg = "Preview Edition generation failed";
        try {
          const d = await resp.json();
          msg = (d && (d.detail?.message || d.detail)) || msg;
        } catch (e) { /* keep default */ }
        alert(msg);
        return;
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const _cn = (report?.company_data?.company_name || "").replace(/\s+/g, "_");
      a.download = _cn ? `${_cn}_ESG_Preview_Edition.pdf` : `ESG_Preview_Edition.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      alert("Preview Edition download failed: " + e.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleDownload = async (pdfLang) => {
    setDownloadingLang(pdfLang || null);
    setDownloading(true);
    try { await api.downloadPdf(id, pdfLang); }
    catch (err) { alert("Download failed: " + err.message); }
    finally { setDownloading(false); setDownloadingLang(null); }
  };

  // Mobile detection, on touch devices we render the download as a real
  // <a href> link. iOS Safari + Android Chrome handle the navigation natively
  // and respect the backend's `Content-Disposition: attachment` header,
  // triggering a real file download (no in-browser preview, no JS-blob magic
  // that mobile WebKit silently drops).
  const isMobile = typeof navigator !== 'undefined' && /iPhone|iPad|iPod|Android|Mobile/i.test(navigator.userAgent);
  // Compare EN vs AR entry, only gate on VIEWPORT width, not UA. Some
  // desktop browsers (dev-tools emulation, older Chrome, split-screen)
  // still get a "Mobile" UA token but have plenty of horizontal room
  // for the side-by-side comparer. Viewport width is the honest signal.
  const [isCompareBlocked, setIsCompareBlocked] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 767px)');
    const onChange = (e) => setIsCompareBlocked(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  const handleTranslate = async () => {
    const reportLang = report?.language || "en";
    const targetLang = reportLang === "en" ? "ar" : "en";
    const targetName = targetLang === "ar" ? "Arabic" : "English";
    if (!window.confirm(`Translate this report to ${targetName}? This may take a few minutes.`)) return;
    setTranslating(true);
    try {
      const result = await api.translateReport(id, targetLang);
      if (result.status === "already_translated") {
        // Already done, just refresh
        const updated = await api.getReport(id);
        setReport(updated);
      } else {
        // Poll for completion
        await api.pollUntilTranslated(id, () => {});
        const updated = await api.getReport(id);
        setReport(updated);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message || err.message || "Translation failed");
      alert("Translation failed: " + msg);
    }
    finally { setTranslating(false); }
  };

  const handleRegenerate = async () => {
    if (!window.confirm("Regenerate the AI prose using the same input data? This rewrites all narrative sections, your form data is unchanged. Takes 12–20 min.")) return;
    setRegenerating(true);
    setRegenElapsed(0);
    setRegenPhase("init");
    setRegenStatus("Starting…");
    // Compute elapsed from a fixed start timestamp so the counter shows the
    // REAL elapsed time even when the tab was backgrounded or the user
    // navigated away and came back. Without this, browsers throttle
    // setInterval in background tabs, freezing the counter.
    const startTs = Date.now();
    const timer = setInterval(() => {
      setRegenElapsed(Math.floor((Date.now() - startTs) / 1000));
    }, 1000);
    // Scroll the progress card into view immediately so user sees something
    // is happening, do it after the next frame so the element is mounted.
    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    });
    try {
      const result = await api.pollUntilGenerated(id, (status, _attempts, phaseLabel, phaseId) => {
        if (status === "generating") {
          setRegenStatus(phaseLabel ? `${phaseLabel}…` : "AI is rewriting your report…");
          if (phaseId) setRegenPhase(phaseId);
        } else if (status === "generated") {
          setRegenStatus("Complete!");
          setRegenPhase("done");
        }
      });
      // If the 20-min poll window expired but server is still generating,
      // give the user a friendly heads-up (not a red "Failed" error).
      if (result && result._polling_timeout) {
        alert("Your report is taking longer than usual but is still being regenerated in the background.\n\nRefresh this page in a minute, your updated PDF will be ready to download. Your data is safe.");
      } else {
        const updated = await api.getReport(id);
        setReport(updated);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail?.message || err.message || "Regeneration failed");
      alert("Failed: " + msg);
    }
    finally {
      clearInterval(timer);
      setRegenerating(false);
    }
  };

  const formatElapsed = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m}m ${sec.toString().padStart(2, "0")}s` : `${sec}s`;
  };

  const handleSaveBranding = async () => {
    setSavingBrand(true);
    try {
      await api.updateBranding(id, brandColors);
      await api.updateReport(id, { company_data: { ...report.company_data, cover_color: coverColor, cover_style: coverStyle } });
      if (logoFile) {
        await api.uploadLogo(id, logoFile);
        setLogoName(logoFile.name);
        setLogoFile(null);
      }
      if (coverImageFile) {
        await api.uploadSectionImage(id, "cover_image", coverImageFile);
        setCoverImageName(coverImageFile.name);
        setCoverImageFile(null);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      alert("Failed to save: " + (typeof detail === "string" ? detail : (err.message || "Unknown error")));
    }
    finally { setSavingBrand(false); }
  };

  const handleRemoveLogo = async () => {
    try {
      await api.deleteLogo(id);
      setLogoName("");
      setLogoFile(null);
    } catch (err) { console.error(err); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-[#070709]">
      <div className="w-8 h-8 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (!report) return null;

  const company = report.company_data || {};
  const scores = report.scores || {};
  // Display the report body in the currently-selected UI language.
  // - If primary (report.language) matches UI lang → use `ai_content`
  // - Else try the translated copy (`ai_content_ar` / `ai_content_en`)
  // - Else fall back to `ai_content` so user still sees something
  const reportLang = report.language || "en";
  const otherField = lang === "ar" ? "ai_content_ar" : "ai_content_en";
  const translationExists = !!report[otherField];
  const showTranslationBanner = lang !== reportLang && !translationExists;
  const ai = (lang === reportLang)
    ? (report.ai_content || {})
    : (report[otherField] || report.ai_content || {});

  return (
    <div className="min-h-screen" style={{ background: "#070709" }}>
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#070709]/70 border-b border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-3 flex flex-col gap-2">
          {/* Row 1, back link + primary tools (left → right):
              Language, Branding, Edit Data, Regenerate, Compare EN vs AR */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <button onClick={() => navigate("/dashboard")} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors text-sm" data-testid="back-to-dashboard">
              <ArrowCircleLeft size={20} /> Dashboard
            </button>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <LangSwitch />
              <button data-testid="branding-toggle" onClick={() => setShowBranding(!showBranding)}
                className={`flex items-center gap-1.5 transition-colors text-sm border px-3 py-1.5 rounded-lg ${showBranding ? "text-[#01AF9B] border-[#01AF9B]/30 bg-[#01AF9B]/10" : "text-slate-400 hover:text-white border-slate-700 hover:bg-slate-800"}`}>
                <Palette size={14} /> {t('branding')}
              </button>
              <button data-testid="edit-report-button" onClick={() => navigate(`/report/${id}/edit`)}
                className="flex items-center gap-1.5 text-slate-400 hover:text-white transition-colors text-sm border border-slate-700 px-3 py-1.5 rounded-lg hover:bg-slate-800">
                <PencilSimple size={14} /> {t('edit_data')}
              </button>
              <button data-testid="regenerate-button" onClick={handleRegenerate} disabled={regenerating}
                className="flex items-center gap-1.5 text-[#01AF9B] hover:text-white transition-colors text-sm border border-[#01AF9B]/30 px-3 py-1.5 rounded-lg hover:bg-[#01AF9B]/10 disabled:opacity-50">
                <Sparkle size={14} className={regenerating ? "animate-pulse" : ""} />
                {regenerating ? `Regenerating… ${formatElapsed(regenElapsed)}` : t('regenerate')}
              </button>
              {(() => {
                // "Compare EN vs AR" appears only when BOTH language versions
                // of the report exist AND we have enough viewport width for
                // the side-by-side layout to be usable. Gate on viewport
                // width only (>=768px), NOT on UA, desktop browsers with
                // touch mode or exotic UAs still get the feature.
                // Free-tier draft: no bilingual preview at all — the
                // Preview Edition is EN only per product spec, and both
                // panes would spin forever waiting for the paid renderer.
                if (isFreeDraftLocked) return null;
                if (isCompareBlocked) return null;
                const reportLang = report?.language || "en";
                const otherLang = reportLang === "en" ? "ar" : "en";
                const hasTranslation = otherLang === "ar" ? !!report?.has_ar : !!report?.has_en;
                if (!hasTranslation) return null;
                return (
                  <button
                    data-testid="compare-en-ar-button"
                    onClick={() => navigate(`/report/${id}/compare`)}
                    className="flex items-center gap-1.5 text-[#3B82F6] hover:text-white transition-colors text-sm border border-[#3B82F6]/30 px-3 py-1.5 rounded-lg hover:bg-[#3B82F6]/10"
                    title={lang === "ar" ? "فتح العربية والإنجليزية جنباً إلى جنب" : "Open English and Arabic side-by-side"}
                  >
                    <Rows size={14} weight="bold" /> Compare EN vs AR
                  </button>
                );
              })()}
            </div>
          </div>

          {/* Row 2, right-justified download buttons.
              Green pill = English PDF, Orange pill = Arabic PDF.
              When one language isn't generated yet, that colored pill
              becomes the Translate CTA so the user can create it.
              Free-draft users see a single "View blurred preview" pill
              instead of the raw download buttons. */}
          <div className="flex items-center justify-end gap-2 flex-wrap" data-testid="report-download-row">
            {isFreeDraftLocked ? (
              <button
                data-testid="download-light-pdf-top"
                onClick={handleDownloadLightPdf}
                disabled={downloading}
                className="flex items-center gap-2 bg-gradient-to-r from-[#01AF9B] to-[#018F7E] hover:opacity-90 text-white text-sm font-['Outfit'] font-bold px-4 py-2 rounded-lg transition-all disabled:opacity-50"
              >
                <DownloadSimple size={15} weight="bold" /> {lang === "ar" ? "تنزيل النسخة التمهيدية (EN)" : (downloading ? "Preparing…" : "Download Free Preview (EN)")}
              </button>
            ) : (
              <>
            {(() => {
              const reportLang = report?.language || "en";
              // English PDF button (green). Direct download if EN exists.
              // If report is AR primary and EN wasn't translated, show
              // "Translate → EN" in its place.
              const hasEnContent = reportLang === "en" || !!report?.has_en;
              if (hasEnContent) {
                return isMobile ? (
                  <a data-testid="download-pdf-en" href={api.pdfUrl(id, "en")}
                    target="_self" rel="noopener" download
                    className="flex items-center gap-1.5 bg-[#01AF9B] hover:bg-[#018F7E] text-white text-sm font-semibold px-4 py-1.5 rounded-lg transition-all">
                    <DownloadSimple size={16} weight="bold" /> Download PDF
                  </a>
                ) : (
                  <button data-testid="download-pdf-en" onClick={() => handleDownload("en")} disabled={downloading}
                    className="flex items-center gap-1.5 bg-[#01AF9B] hover:bg-[#018F7E] text-white text-sm font-semibold px-4 py-1.5 rounded-lg transition-all disabled:opacity-50">
                    <DownloadSimple size={16} weight="bold" /> {downloadingLang === "en" ? "Preparing..." : "Download PDF"}
                  </button>
                );
              }
              // EN missing, show a Translate → EN pill in the green slot.
              return (
                <button data-testid="translate-to-en" onClick={handleTranslate} disabled={translating}
                  className="flex items-center gap-1.5 text-[#01AF9B] hover:text-white transition-colors text-sm border border-[#01AF9B]/40 px-3 py-1.5 rounded-lg hover:bg-[#01AF9B]/10 disabled:opacity-50">
                  <Translate size={14} /> {translating ? "Translating..." : "Translate → EN"}
                </button>
              );
            })()}
            {(() => {
              const reportLang = report?.language || "en";
              const hasArContent = reportLang === "ar" || !!report?.has_ar;
              if (hasArContent) {
                return isMobile ? (
                  <a data-testid="download-pdf-ar" href={api.pdfUrl(id, "ar")}
                    target="_self" rel="noopener" download
                    className="flex items-center gap-1.5 bg-[#B94B05] hover:bg-[#EA580C] text-white text-sm font-semibold px-4 py-1.5 rounded-lg transition-all">
                    <DownloadSimple size={16} weight="bold" /> تحميل PDF
                  </a>
                ) : (
                  <button data-testid="download-pdf-ar" onClick={() => handleDownload("ar")} disabled={downloading}
                    className="flex items-center gap-1.5 bg-[#B94B05] hover:bg-[#EA580C] text-white text-sm font-semibold px-4 py-1.5 rounded-lg transition-all disabled:opacity-50">
                    <DownloadSimple size={16} weight="bold" /> {downloadingLang === "ar" ? "Preparing..." : "تحميل PDF"}
                  </button>
                );
              }
              return (
                <button data-testid="translate-to-ar" onClick={handleTranslate} disabled={translating}
                  className="flex items-center gap-1.5 text-[#B94B05] hover:text-white transition-colors text-sm border border-[#B94B05]/40 px-3 py-1.5 rounded-lg hover:bg-[#B94B05]/10 disabled:opacity-50">
                  <Translate size={14} /> {translating ? "Translating..." : "Translate → عربي"}
                </button>
              );
            })()}
            <button data-testid="regenerate-ceo-letter" onClick={handleRegenerateCeo} disabled={ceoBusy}
              title={lang === "ar" ? "إعادة كتابة رسالة الرئيس التنفيذي باستخدام أرقامك الفعلية" : "Rewrite the CEO letter using your real figures"}
              className="flex items-center gap-1.5 text-slate-300 hover:text-white transition-colors text-sm border border-white/15 px-3 py-1.5 rounded-lg hover:bg-white/5 disabled:opacity-50">
              <PencilSimple size={14} /> {ceoBusy ? (lang === "ar" ? "جارٍ الكتابة..." : "Rewriting...") : (lang === "ar" ? "إعادة كتابة رسالة الرئيس" : "Rewrite CEO letter")}
            </button>
              </>
            )}
          </div>
          {ceoMsg && <p data-testid="regenerate-ceo-letter-msg" className="w-full text-xs text-slate-300 mt-2">{ceoMsg}</p>}
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8">
        {/* Phased progress card during Regenerate, full-width, top-of-page,
            so the user can clearly see it isn't a no-op like before. */}
        {regenerating && (
          <div className="mb-6 animate-fade-in-up" data-testid="regen-progress-card">
            <GenerationProgress
              phase={regenPhase}
              elapsed={regenElapsed}
              language={lang}
              statusText={regenStatus}
            />
          </div>
        )}

        {/* Report Header */}
        <div className="bg-gradient-to-br from-[#0D1117] to-[#111216] border border-slate-800/60 rounded-2xl p-8 mb-6 relative overflow-hidden animate-fade-in-up" data-testid="report-header">
          <div className="absolute top-[-60px] right-[-60px] w-[250px] h-[250px] rounded-full border border-[#01AF9B]/10" />
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#01AF9B] via-[#B94B05] to-[#3B82F6]" />
          <div className="font-['Outfit'] text-[9px] font-bold tracking-[0.3em] uppercase text-[#01AF9B] mb-4">{t('rv_eyebrow')}</div>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold text-white mb-2">{company.name || "Company"}</h1>
          <p className="text-slate-400 text-sm">{company.sector} &bull; {company.country} &bull; {company.year}</p>
          <div className="flex gap-2 mt-4">
            <span className="text-[10px] font-['Outfit'] font-semibold px-3 py-1 rounded-full bg-[#01AF9B]/10 text-[#01AF9B] border border-[#01AF9B]/20">{t('badge_adx_aligned')}</span>
            <span className="text-[10px] font-['Outfit'] font-semibold px-3 py-1 rounded-full bg-[#B94B05]/10 text-[#B94B05] border border-[#B94B05]/20">{t('badge_tcfd_framework')}</span>
            <span className="text-[10px] font-['Outfit'] font-semibold px-3 py-1 rounded-full bg-[#3B82F6]/10 text-[#3B82F6] border border-[#3B82F6]/20">{t('badge_gri_standards')}</span>
          </div>
        </div>

        {/* Scores */}
        <div className="grid grid-cols-4 gap-4 mb-6 animate-fade-in-up stagger-1" data-testid="scores-row">
          <ScoreCard label={t('score_environmental')} score={scores.environmental} color="#01AF9B" />
          <ScoreCard label={t('score_social')} score={scores.social} color="#B94B05" />
          <ScoreCard label={t('score_governance')} score={scores.governance} color="#1E3A5F" />
          <ScoreCard label={t('score_overall')} score={scores.overall} color="#F8FAFC" />
        </div>

        {/* AI Content Sections */}
        {showBranding && (
          <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-6 mb-6 animate-fade-in-up" data-testid="branding-panel">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-9 h-9 rounded-lg bg-[#01AF9B]/10 border border-[#01AF9B]/20 flex items-center justify-center">
                <Palette size={18} className="text-[#01AF9B]" />
              </div>
              <div>
                <h3 className="font-['Outfit'] text-base font-bold text-white">{lang === "ar" ? "علامة التقرير التجارية" : "Report Branding"}</h3>
                <p className="text-slate-500 text-xs">{lang === "ar" ? "خصّص ملف PDF بشعار علامتك وألوانها" : "Customize the PDF with your brand logo and colors"}</p>
              </div>
            </div>

            {/* Logo Upload */}
            <div className="mb-5">
              <label className="font-['Outfit'] text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400 block mb-2">{lang === "ar" ? "شعار الشركة" : "Company Logo"}</label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 bg-[#0B0C10] border border-slate-700 rounded-lg px-4 py-2.5 text-sm text-slate-300 cursor-pointer hover:border-[#01AF9B]/50 transition-all" data-testid="logo-upload-btn">
                  <UploadSimple size={16} className="text-[#01AF9B]" />
                  <span>{logoFile ? logoFile.name : "Upload Logo"}</span>
                  <input type="file" accept="image/*" className="hidden" onChange={e => setLogoFile(e.target.files[0])} />
                </label>
                {logoName && !logoFile && (
                  <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Image size={14} className="text-[#01AF9B]" />
                    <span>{logoName}</span>
                    <button onClick={handleRemoveLogo} className="text-red-400 hover:text-red-300 ml-1" data-testid="remove-logo-btn"><Trash size={14} /></button>
                  </div>
                )}
              </div>
              <p className="text-slate-600 text-[10px] mt-1">PNG or JPG, max 2MB. Appears on cover page and back page.</p>
            </div>

            {/* Cover Style Selector */}
            <div className="mb-5">
              <label className="font-['Outfit'] text-[10px] font-semibold tracking-[0.08em] uppercase text-slate-400 block mb-3">{lang === "ar" ? "نمط صفحة الغلاف" : "Cover Page Style"}</label>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {/* Option 1: Preset */}
                <button onClick={() => setCoverStyle("preset")} data-testid="cover-style-preset"
                  className={`relative rounded-xl border-2 p-3 transition-all text-left ${coverStyle === "preset" ? "border-[#01AF9B] bg-[#01AF9B]/5" : "border-slate-700 hover:border-slate-500"}`}>
                  <div className="w-full h-16 rounded-lg mb-2 overflow-hidden bg-slate-800">
                    <img src="/preset_thumb.jpg" alt="" className="w-full h-full object-cover opacity-60" />
                  </div>
                  <div className="font-['Outfit'] text-xs font-bold text-slate-200">{lang === "ar" ? "صورة جاهزة" : "Preset Image"}</div>
                  <div className="text-[9px] text-slate-500">Our hero image + your logo</div>
                  {coverStyle === "preset" && <div className="absolute top-2 right-2 w-4 h-4 bg-[#01AF9B] rounded-full flex items-center justify-center"><span className="text-white text-[8px] font-bold">&#10003;</span></div>}
                </button>
                {/* Option 2: Solid Color */}
                <button onClick={() => setCoverStyle("solid")} data-testid="cover-style-solid"
                  className={`relative rounded-xl border-2 p-3 transition-all text-left ${coverStyle === "solid" ? "border-[#01AF9B] bg-[#01AF9B]/5" : "border-slate-700 hover:border-slate-500"}`}>
                  <div className="w-full h-16 rounded-lg mb-2" style={{ background: coverColor }}></div>
                  <div className="font-['Outfit'] text-xs font-bold text-slate-200">{lang === "ar" ? "لون سادة" : "Solid Color"}</div>
                  <div className="text-[9px] text-slate-500">{lang === "ar" ? "اختر لون علامتك" : "Pick your brand color"}</div>
                  {coverStyle === "solid" && <div className="absolute top-2 right-2 w-4 h-4 bg-[#01AF9B] rounded-full flex items-center justify-center"><span className="text-white text-[8px] font-bold">&#10003;</span></div>}
                </button>
                {/* Option 3: Custom Image */}
                <button onClick={() => setCoverStyle("custom")} data-testid="cover-style-custom"
                  className={`relative rounded-xl border-2 p-3 transition-all text-left ${coverStyle === "custom" ? "border-[#01AF9B] bg-[#01AF9B]/5" : "border-slate-700 hover:border-slate-500"}`}>
                  <div className="w-full h-16 rounded-lg mb-2 bg-slate-800 flex items-center justify-center border border-dashed border-slate-600">
                    <UploadSimple size={20} className="text-slate-500" />
                  </div>
                  <div className="font-['Outfit'] text-xs font-bold text-slate-200">{lang === "ar" ? "صورة مخصّصة" : "Custom Image"}</div>
                  <div className="text-[9px] text-slate-500">{lang === "ar" ? "ارفع غلافك الخاص" : "Upload your own cover"}</div>
                  {coverStyle === "custom" && <div className="absolute top-2 right-2 w-4 h-4 bg-[#01AF9B] rounded-full flex items-center justify-center"><span className="text-white text-[8px] font-bold">&#10003;</span></div>}
                </button>
              </div>

              {/* Solid Color picker, show only for solid and custom */}
              {(coverStyle === "solid" || coverStyle === "custom") && (
                <div className="mb-4">
                  <label className="font-['Outfit'] text-[9px] font-semibold tracking-[0.06em] uppercase text-slate-500 block mb-2">
                    {coverStyle === "custom" ? "Back Cover Color" : "Cover Page Color"}
                  </label>
                  <div className="flex items-center gap-4">
                    <input type="color" data-testid="color-cover" value={coverColor}
                      onChange={e => setCoverColor(e.target.value)}
                      className="w-12 h-12 rounded-xl border-2 border-slate-600 cursor-pointer bg-transparent hover:border-[#01AF9B] transition-all" />
                    <input type="text" data-testid="cover-hex-input" value={coverColor}
                      onChange={e => { const v = e.target.value; if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) setCoverColor(v); }}
                      onBlur={e => { const v = e.target.value; if (!/^#[0-9A-Fa-f]{6}$/.test(v)) setCoverColor("#73324B"); }}
                      className="w-28 bg-[#0B0C10] border border-slate-700 text-slate-100 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-[#01AF9B] focus:border-transparent outline-none"
                      placeholder="#73324B" maxLength={7} />
                    <div className="w-16 h-12 rounded-lg border border-slate-700" style={{ background: coverColor }} />
                  </div>
                </div>
              )}

              {/* Custom image upload, show only for custom */}
              {coverStyle === "custom" && (
                <div className="mb-4">
                  <label className="font-['Outfit'] text-[9px] font-semibold tracking-[0.06em] uppercase text-slate-500 block mb-2">{lang === "ar" ? "ارفع خلفية الغلاف" : "Upload Cover Background"}</label>
                  <label className="flex items-center gap-2 bg-[#0B0C10] border border-slate-700 rounded-lg px-4 py-2.5 cursor-pointer hover:border-slate-500 transition-colors text-sm text-slate-400 w-fit">
                    <UploadSimple size={16} /> {coverImageFile ? coverImageFile.name : coverImageName || "Choose image..."}
                    <input type="file" accept="image/*" className="hidden" data-testid="cover-image-upload"
                      onChange={e => { if (e.target.files?.[0]) setCoverImageFile(e.target.files[0]); }} />
                  </label>
                  <p className="text-slate-600 text-[9px] mt-1">High-resolution image recommended (max 5MB). Image will be cropped to fit A4 page.</p>
                </div>
              )}
            </div>

            <button data-testid="save-branding-btn" onClick={handleSaveBranding} disabled={savingBrand}
              className="bg-[#01AF9B] hover:bg-[#018F7E] text-white font-['Outfit'] font-semibold py-2.5 px-5 rounded-lg transition-all flex items-center gap-2 text-sm disabled:opacity-50">
              {savingBrand ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <Palette size={16} />}
              {savingBrand ? "Saving..." : "Save Branding"}
            </button>
          </div>
        )}
        {showTranslationBanner && report?.translation_status === "translating" && (
          <div className="bg-gradient-to-br from-[#01AF9B]/15 to-[#01AF9B]/5 border border-[#01AF9B]/30 rounded-xl p-5 sm:p-6 mb-6 animate-fade-in-up" data-testid="translation-in-progress-banner">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#01AF9B]/20 border border-[#01AF9B]/40 flex items-center justify-center flex-shrink-0">
                <div className="w-5 h-5 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-['Outfit'] text-base sm:text-lg font-bold text-white mb-1">
                  {lang === "ar" ? "جارٍ إعداد النسخة الأخرى" : "Preparing the other language"}
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {lang === "ar"
                    ? "نقوم بإنشاء كلتا اللغتين تلقائيًا. سيتم تحديث هذه الصفحة بمجرد اكتمالها (عادة 5-10 دقائق)."
                    : "Both language editions are generating automatically. This page will refresh as soon as the other one is ready (usually 5-10 min)."}
                </p>
              </div>
            </div>
          </div>
        )}
        {showTranslationBanner && report?.translation_status !== "translating" && (
          <div className="bg-gradient-to-br from-[#B94B05]/20 to-[#B94B05]/5 border border-[#B94B05]/40 rounded-xl p-5 sm:p-6 mb-6 animate-fade-in-up" data-testid="translation-missing-banner">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#B94B05]/20 border border-[#B94B05]/40 flex items-center justify-center flex-shrink-0">
                <Translate size={20} className="text-[#B94B05]" weight="bold" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-['Outfit'] text-base sm:text-lg font-bold text-white mb-1">
                  {lang === "ar" ? t('translation_missing_title') : t('translation_missing_en_title')}
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed mb-4">
                  {lang === "ar" ? t('translation_missing_desc') : t('translation_missing_en_desc')}
                </p>
                <button onClick={handleTranslate} disabled={translating} data-testid="translate-banner-cta"
                  className="bg-[#B94B05] hover:bg-[#EA580C] disabled:opacity-60 disabled:cursor-not-allowed text-white font-['Outfit'] font-semibold text-sm py-2.5 px-5 rounded-lg transition-all inline-flex items-center gap-2">
                  <Translate size={16} weight="bold" />
                  {translating
                    ? t('translation_in_progress')
                    : (lang === "ar" ? t('translation_missing_cta') : t('translation_missing_en_cta'))}
                </button>
              </div>
            </div>
          </div>
        )}
        {ai.executive_summary && <Section title={t('sec_executive_summary')} content={ai.executive_summary} />}
        {ai.ceo_letter && <Section title={t('sec_ceo_letter')} content={ai.ceo_letter} />}
        {ai.company_overview && <Section title={t('sec_company_overview')} content={ai.company_overview} />}
        {ai.materiality_assessment && <Section title={t('sec_materiality')} content={ai.materiality_assessment} />}

        <SectionDivider title={t('div_environmental')} color="#01AF9B" />
        {ai.environmental_overview && <Section title={t('sec_env_strategy')} content={ai.environmental_overview} color="#01AF9B" />}
        {ai.ghg_emissions && <Section title={t('sec_ghg')} content={ai.ghg_emissions} color="#01AF9B" />}
        {ai.energy_management && <Section title={t('sec_energy')} content={ai.energy_management} color="#01AF9B" />}
        {ai.water_stewardship && <Section title={t('sec_water')} content={ai.water_stewardship} color="#01AF9B" />}
        {ai.waste_management && <Section title={t('sec_waste')} content={ai.waste_management} color="#01AF9B" />}
        {ai.climate_risk_tcfd && <Section title={t('sec_climate_tcfd')} content={ai.climate_risk_tcfd} color="#01AF9B" />}
        {ai.environmental_targets && <Section title={t('sec_env_targets')} content={ai.environmental_targets} color="#01AF9B" />}

        <SectionDivider title={t('div_social')} color="#B94B05" />
        {ai.social_overview && <Section title={t('sec_social_strategy')} content={ai.social_overview} color="#B94B05" />}
        {ai.workforce_diversity && <Section title={t('sec_workforce')} content={ai.workforce_diversity} color="#B94B05" />}
        {ai.health_safety && <Section title={t('sec_health')} content={ai.health_safety} color="#B94B05" />}
        {ai.training_development && <Section title={t('sec_training')} content={ai.training_development} color="#B94B05" />}
        {ai.community_engagement && <Section title={t('sec_community')} content={ai.community_engagement} color="#B94B05" />}
        {ai.supply_chain && <Section title={t('sec_supply')} content={ai.supply_chain} color="#B94B05" />}

        <SectionDivider title={t('div_governance')} color="#1E3A5F" />
        {ai.governance_overview && <Section title={t('sec_gov_framework')} content={ai.governance_overview} color="#1E3A5F" />}
        {ai.board_composition && <Section title={t('sec_board')} content={ai.board_composition} color="#1E3A5F" />}
        {ai.ethics_compliance && <Section title={t('sec_ethics')} content={ai.ethics_compliance} color="#1E3A5F" />}
        {ai.data_privacy && <Section title={t('sec_privacy')} content={ai.data_privacy} color="#1E3A5F" />}
        {ai.esg_governance && <Section title={t('sec_esg_gov')} content={ai.esg_governance} color="#1E3A5F" />}

        <SectionDivider title={t('div_appendices')} color="#94A3B8" />
        {ai.stakeholder_engagement && <Section title={t('sec_stakeholder')} content={ai.stakeholder_engagement} />}
        {ai.sdg_alignment && <Section title={t('sec_sdg')} content={ai.sdg_alignment} />}
        {ai.methodology && <Section title={t('sec_methodology')} content={ai.methodology} />}
        {ai.forward_looking && <Section title={t('sec_forward')} content={ai.forward_looking} />}

        {/* Download CTA */}
        <div className="text-center py-10 animate-fade-in-up">
          {isFreeDraftLocked ? (
            <div className="max-w-2xl mx-auto bg-gradient-to-br from-[#F97316]/15 to-[#01AF9B]/10 border border-[#F97316]/40 rounded-2xl p-6 sm:p-8" data-testid="freemium-cta-bottom">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F97316]/20 border border-[#F97316]/40 text-[#F97316] text-xs font-['Outfit'] font-bold tracking-wide uppercase mb-3">
                <Lock size={13} weight="fill" /> {lang === "ar" ? "المسودّة مجانية" : "Free draft"}
              </div>
              <h3 className="font-['Outfit'] text-xl sm:text-2xl font-bold text-white mb-2">
                {lang === "ar" ? "تنزيل النسخة التمهيدية المجانية أو الاشتراك لفتح النسخة الكاملة" : "Download your free Preview Edition, or unlock the full report"}
              </h3>
              <p className="text-sm text-slate-300 leading-relaxed mb-5">
                {lang === "ar"
                  ? "نسختك التمهيدية المجانية عبارة عن تقرير من 8 صفحات باللغة الإنجليزية مع علامة مائية. قم بالترقية بـ 499 $ / سنة للحصول على ملف PDF كامل بأكثر من 60 صفحة باللغتين الإنجليزية والعربية، مع تعديلات وتقارير غير محدودة طوال العام."
                  : "Your free Preview Edition is a watermarked 8-page English PDF that gives you a taste of the full platform. Upgrade for $499/year to unlock the complete 60+ page bilingual report (EN + AR), with unlimited generations and amendments for the year."}
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <button
                  data-testid="download-light-pdf-bottom"
                  onClick={handleDownloadLightPdf}
                  disabled={downloading}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#01AF9B] to-[#018F7E] hover:opacity-90 text-white border border-[#01AF9B]/40 font-['Outfit'] font-semibold text-sm py-3 px-5 rounded-xl transition-all disabled:opacity-50"
                >
                  <DownloadSimple size={16} weight="bold" /> {lang === "ar" ? "تنزيل النسخة التمهيدية المجانية (EN)" : (downloading ? "Preparing…" : "Download Free Preview Edition (EN)")}
                </button>
                <button
                  data-testid="freemium-subscribe-bottom"
                  onClick={() => navigate("/billing")}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#01AF9B] to-[#018F7E] hover:opacity-90 text-white font-['Outfit'] font-bold text-base py-3 px-6 rounded-xl transition-all"
                >
                  <Lightning size={18} weight="fill" /> {lang === "ar" ? "اشترك بـ 499 $ / سنة" : "Subscribe $499/year"}
                </button>
              </div>
            </div>
          ) : (
          <>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
            {isMobile ? (
              <a data-testid="download-pdf-bottom" href={api.pdfUrl(id)}
                target="_self" rel="noopener" download
                className="bg-gradient-to-r from-[#01AF9B] to-[#018F7E] text-white font-['Outfit'] font-bold text-base py-4 px-8 rounded-xl transition-all hover:opacity-90 inline-flex items-center gap-2">
                <DownloadSimple size={20} weight="bold" /> {`Download PDF (${(report?.language || 'en').toUpperCase()})`}
              </a>
            ) : (
              <button data-testid="download-pdf-bottom" onClick={() => handleDownload()} disabled={downloading}
                className="bg-gradient-to-r from-[#01AF9B] to-[#018F7E] text-white font-['Outfit'] font-bold text-base py-4 px-8 rounded-xl transition-all hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
                <DownloadSimple size={20} weight="bold" /> {downloading && !downloadingLang ? "Preparing..." : `Download PDF (${(report?.language || 'en').toUpperCase()})`}
              </button>
            )}
            {(() => {
              const reportLang = report?.language || "en";
              const otherLang = reportLang === "en" ? "ar" : "en";
              const otherName = otherLang === "ar" ? "Arabic (عربي)" : "English";
              const hasTranslation = otherLang === "ar" ? !!report?.has_ar : !!report?.has_en;
              if (!hasTranslation) return null;
              return isMobile ? (
                <a data-testid="download-pdf-translated" href={api.pdfUrl(id, otherLang)}
                  target="_self" rel="noopener" download
                  className="bg-gradient-to-r from-[#B94B05] to-[#EA580C] text-white font-['Outfit'] font-bold text-base py-4 px-8 rounded-xl transition-all hover:opacity-90 inline-flex items-center gap-2">
                  <DownloadSimple size={20} weight="bold" /> {`Download PDF (${otherName})`}
                </a>
              ) : (
                <button data-testid="download-pdf-translated" onClick={() => handleDownload(otherLang)} disabled={downloading}
                  className="bg-gradient-to-r from-[#B94B05] to-[#EA580C] text-white font-['Outfit'] font-bold text-base py-4 px-8 rounded-xl transition-all hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
                  <DownloadSimple size={20} weight="bold" /> {downloadingLang === otherLang ? "Preparing..." : `Download PDF (${otherName})`}
                </button>
              );
            })()}
          </div>
          <p className="text-slate-500 text-xs">{t('pdf_desc')}</p>
          {(() => {
            const reportLang = report?.language || "en";
            const otherLang = reportLang === "en" ? "ar" : "en";
            const hasTranslation = otherLang === "ar" ? !!report?.ai_content_ar : !!report?.ai_content_en;
            if (hasTranslation) return null;
            return (
              <p className="text-slate-600 text-xs mt-2">
                Want the report in {otherLang === "ar" ? "Arabic" : "English"}? Click
                <button onClick={handleTranslate} disabled={translating} className="text-[#B94B05] hover:text-[#B94B05]/80 mx-1 font-semibold">
                  {translating ? "translating..." : `"Translate → ${otherLang === "ar" ? "عربي" : "EN"}"`}
                </button>
                above.
              </p>
            );
          })()}
          </>
          )}
        </div>
      </main>
      <BlurredPreview
        reportId={id}
        isOpen={showBlurredPreview}
        onClose={() => setShowBlurredPreview(false)}
        expiresAt={sub?.free_draft_expires_at}
      />
    </div>
  );
}

function ScoreCard({ label, score, color }) {
  return (
    <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-4 text-center hover:border-slate-700 transition-all">
      <div className="font-['Outfit'] text-2xl sm:text-3xl font-bold" style={{ color }}>{score || 0}%</div>
      <div className="font-['Outfit'] text-[9px] font-semibold tracking-[0.1em] uppercase text-slate-500 mt-1">{label}</div>
    </div>
  );
}

function SectionDivider({ title, color }) {
  return (
    <div className="flex items-center gap-3 my-8 animate-fade-in-up">
      <div className="w-3 h-3 rounded-full" style={{ background: color }} />
      <span className="font-['Outfit'] text-lg font-bold text-white">{title}</span>
      <div className="flex-1 h-px bg-slate-800" />
    </div>
  );
}

function Section({ title, content, color = "#01AF9B" }) {
  const paragraphs = content.split("\n\n").filter(Boolean);
  return (
    <div className="bg-[#111216] border border-slate-800/60 rounded-xl p-6 mb-4 hover:border-slate-700 transition-all animate-fade-in-up">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-2 h-2 rounded-full" style={{ background: color }} />
        <h3 className="font-['Outfit'] text-base font-bold text-white">{title}</h3>
      </div>
      <div className="space-y-3">
        {paragraphs.map((p, i) => (
          <p key={i} className="text-slate-300 text-sm leading-relaxed">{p.trim()}</p>
        ))}
      </div>
    </div>
  );
}
